/**
 * Shared table formatting utilities
 *
 * Extracted from BaseReportPage.tsx to be reusable across
 * platform reports and inspector exception tables.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { NUMERIC_FONT_SX } from '../theme/numericFont'
import {
  Box,
  Chip,
} from '@mui/material'
import {
  Launch as LaunchIcon,
} from '@mui/icons-material'
import {
  handleLightspeedLinkClick,
  handleXeroLinkClick,
  handleExternalLinkClick,
  POPUP_CONFIGS,
} from './externalLinks'
import { getFreshnessTier } from './syncFreshness'

// Re-export types used by consumers
export type { ColumnLink, CellError, BooleanLabels, ColorBand, ColumnDef } from '../pages/admin/PlatformReports/reports/BaseReportPage'

// Import types for internal use
import type { ColumnLink, CellError, ColorBand, ColumnDef } from '../pages/admin/PlatformReports/reports/BaseReportPage'

// Row-level highlight colors
export const ROW_HIGHLIGHT_COLORS: Record<string, string> = {
  error: '#FFEBEE',
  success: '#E8F5E9',
  inactive: '#F5F5F5',
}

const INACTIVE_TEXT_COLOR = '#9E9E9E'

const SYNC_STALE_ERROR_DAYS = 5

/**
 * Format a datetime as a compact relative time string (e.g., "2h", "5d")
 */
export const formatTimeAgo = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '--'
  const hasTz = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr)
  const date = new Date(hasTz ? dateStr : `${dateStr}Z`)
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 60) return `${Math.max(1, diffMinutes)}m`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h`
  return `${Math.floor(diffHours / 24)}d`
}

/**
 * Format a datetime as a Data Age value for sync_status chips.
 *
 * Always days, "0d" for <24h, capped at the red-chip threshold so the value
 * cap and the colour change track together. Centralised so every report
 * (platform reports, exception reports) renders staleness identically.
 */
export const formatDataAge = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '--'
  const date = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`)
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays >= SYNC_STALE_ERROR_DAYS) return `${SYNC_STALE_ERROR_DAYS}d+`
  return `${Math.max(0, diffDays)}d`
}

/**
 * Get chip colors based on sync staleness.
 *
 * Driven by the canonical 4-tier ladder in `syncFreshness.ts` so the
 * platform reports' "Data Age" column reads the same as the inspector
 * nav chips, freshness banner, dashboard hero, and health snapshot.
 */
export const getSyncStatusColor = (dateStr: string | null): { bg: string; text: string } | null => {
  if (!dateStr) return { bg: '#F5F5F5', text: '#757575' }
  const tier = getFreshnessTier(dateStr)
  switch (tier) {
    case 'critical':
      return { bg: '#FFEBEE', text: '#C62828' }
    case 'attention':
      return { bg: '#FFF3E0', text: '#E65100' }
    case 'warning':
      return { bg: '#FFF8E1', text: '#7A4F01' }
    case 'fresh':
    default:
      return null
  }
}

/**
 * FP Status chip colors
 */
export const getFpStatusColor = (status: string): 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'Active':
      return 'success'
    case 'Onboarding':
      return 'info'
    case 'Terminated':
      return 'error'
    case 'Test':
    default:
      return 'default'
  }
}

/**
 * Format a cell value based on its type and column configuration
 */
export const formatValue = (value: any, col: ColumnDef): string => {
  if (value === null || value === undefined) return '-'

  switch (col.type) {
    case 'currency':
      return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    case 'business_date':
      if (typeof value === 'string') {
        const bdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (bdMatch) {
          const [, yr, mo, dy] = bdMatch
          const bd = new Date(parseInt(yr), parseInt(mo) - 1, parseInt(dy))
          return bd.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
        }
      }
      return String(value)
    case 'date':
      if (typeof value === 'string') {
        const date = new Date(value)
        return date.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      }
      return String(value)
    case 'datetime':
      if (typeof value === 'string') {
        const date = new Date(value)
        return date.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      }
      return String(value)
    case 'percentage':
      return `${(Number(value) * 100).toFixed(0)}%`
    case 'integer':
      return Number(value).toLocaleString()
    case 'boolean': {
      const labels = col.booleanLabels || { true: 'Yes', false: 'No' }
      return value ? labels.true : labels.false
    }
    case 'boolean_indicator':
      return value ? 'Yes' : 'No'
    default:
      return String(value)
  }
}

/**
 * Get column alignment based on column definition
 */
export const getColumnAlignment = (col: ColumnDef): 'left' | 'center' | 'right' => {
  if (col.align) return col.align
  if (col.key === 'franchise_name' || col.key === 'fp_name') return 'left'
  if (['currency', 'percentage', 'integer'].includes(col.type || '')) return 'right'
  return 'center'
}

/**
 * Resolve URL from column link configuration
 */
export const resolveUrl = (link: ColumnLink, row: Record<string, any>): string | null => {
  if (link.builder) {
    return link.builder(row) ?? null
  }
  if (link.field) {
    const url = row[link.field]
    return url ? String(url) : null
  }
  if (link.template) {
    return link.template.replace(/\{(\w+)\}/g, (_, field) => {
      const val = row[field]
      return val !== null && val !== undefined ? String(val) : ''
    })
  }
  return null
}

/**
 * Determine if a URL is external (opens in new tab/popup)
 */
export const isExternalUrl = (url: string, link: ColumnLink): boolean => {
  if (link.external !== undefined) return link.external
  if (link.system) return true
  return url.startsWith('http://') || url.startsWith('https://')
}

/**
 * Check if a cell value matches any error condition. ``row`` is required
 * for the ``rowFlag`` rule, which reads a server-derived boolean from the
 * row dict (e.g. ``account_code_invalid`` for Xero code columns).
 */
export const hasError = (value: any, error?: CellError, row?: Record<string, any>): boolean => {
  if (!error) return false

  if (error.empty) {
    if (value === null || value === undefined) return true
    if (typeof value === 'string' && value.trim().length < 3) return true
  }

  if (error.zero) {
    if (value === 0 || value === '0') return true
  }

  if (error.thresholdPercent !== undefined) {
    const numValue = typeof value === 'number' ? value : parseFloat(value)
    if (!isNaN(numValue) && Math.abs(numValue) > error.thresholdPercent) return true
  }

  // Server-derived per-row boolean. Highlight logic that needs cross-field
  // context (e.g. "code looks valid by format but doesn't exist in xo_coa
  // for this FP") lives in the report query; the table just trusts the flag.
  if (error.rowFlag && row && row[error.rowFlag]) {
    return true
  }

  return false
}

/**
 * Format cell value with optional link wrapping or special rendering
 */
export const formatCellValue = (
  value: any,
  col: ColumnDef,
  row?: Record<string, any>
): React.ReactNode => {
  const formattedValue = formatValue(value, col)

  if (col.type === 'sync_status') {
    const colors = getSyncStatusColor(value)
    const chipSx: Record<string, any> = {
      height: 22,
      fontSize: '0.75rem',
      fontWeight: 600,
    }
    if (colors) {
      chipSx.backgroundColor = colors.bg
      chipSx.color = colors.text
    }
    return React.createElement(Chip, {
      label: formatDataAge(value),
      size: 'small' as const,
      sx: chipSx,
    })
  }

  if (col.type === 'boolean_indicator') {
    if (!value) return '-'
    return React.createElement(Box, {
      component: 'span',
      sx: {
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: '#4caf50',
      },
    })
  }

  if (col.key === 'fp_status' && value) {
    return React.createElement(Chip, {
      label: value,
      size: 'small' as const,
      color: getFpStatusColor(value),
      sx: { height: 22, fontSize: '0.75rem' },
    })
  }

  if (!col.link || !row) {
    return formattedValue === '-' ? '-' : formattedValue
  }

  const url = resolveUrl(col.link, row)
  if (!url) return '-'

  const displayText = col.link.text || formattedValue
  const external = isExternalUrl(url, col.link)
  const showIcon = col.link.showIcon !== false && external

  if (!external) {
    const newTabProps = col.link.newTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {}
    return React.createElement(
      Link,
      {
        to: url,
        style: { color: '#0563C1', textDecoration: 'none' },
        ...newTabProps,
        onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline' },
        onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLElement).style.textDecoration = 'none' },
      },
      displayText
    )
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (col.link?.system === 'lightspeed') {
      handleLightspeedLinkClick(e, url, String(value))
    } else if (col.link?.system === 'xero') {
      handleXeroLinkClick(e, url, String(value))
    } else {
      handleExternalLinkClick(e, url, String(value), POPUP_CONFIGS.default)
    }
  }

  const children: React.ReactNode[] = [displayText]
  if (showIcon) {
    children.push(
      React.createElement(LaunchIcon, {
        key: 'icon',
        sx: { fontSize: '0.875rem', opacity: 0.7 },
      })
    )
  }

  return React.createElement(
    'a',
    {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: handleClick,
      style: {
        color: '#0563C1',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      },
      onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline' },
      onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLElement).style.textDecoration = 'none' },
    },
    ...children
  )
}

/**
 * Column-specific typography styling for data rows
 */
export const getColumnStyle = (col: ColumnDef, value?: any, rowHighlight?: string, row?: Record<string, any>) => {
  const baseStyle: Record<string, any> = {
    // Canonical numeric type treatment (Inter + tabular-nums) so digits
    // column-align in pricing/quantity columns; harmless on text cells.
    ...NUMERIC_FONT_SX,
    fontSize: '0.8125rem',
    color: '#1F2A2E',
    textAlign: getColumnAlignment(col),
  }

  if (col.colorBands && value != null) {
    const num = typeof value === 'number' ? value : parseFloat(value)
    if (!isNaN(num)) {
      const band = col.colorBands.find(b =>
        (b.min === undefined || num >= b.min) && (b.max === undefined || num <= b.max)
      )
      if (band) {
        baseStyle.backgroundColor = band.bg
        baseStyle.color = band.text
      }
    }
  }

  if (!baseStyle.backgroundColor && hasError(value, col.error, row)) {
    baseStyle.backgroundColor = '#fef0ef'
    baseStyle.color = '#B71C1C'
  } else if (rowHighlight === 'inactive' && !baseStyle.backgroundColor) {
    baseStyle.color = INACTIVE_TEXT_COLOR
  } else if (col.mutedValues && typeof value === 'string' && col.mutedValues.includes(value)) {
    baseStyle.color = INACTIVE_TEXT_COLOR
  }

  if (col.minWidth) {
    baseStyle.minWidth = col.minWidth
  }

  if (col.width) {
    baseStyle.width = col.width
  }

  if (col.maxWidth) {
    baseStyle.maxWidth = col.maxWidth
  }

  if (col.bold) {
    baseStyle.fontWeight = 600
  }

  if (col.key === 'fp_code' && !col.bold) {
    return { ...baseStyle, fontWeight: 600 }
  }
  if ((col.key === 'franchise_name' || col.key === 'fp_name') && !col.minWidth) {
    return { ...baseStyle, fontWeight: col.bold ? 600 : 500, width: 200, minWidth: 200 }
  }

  const secondaryFields = ['city', 'suburb', 'state', 'postcode', 'region']
  if (secondaryFields.includes(col.key) && rowHighlight !== 'inactive') {
    return { ...baseStyle, color: '#4B5A61' }
  }

  return baseStyle
}

/**
 * Format a date for display in report run history
 */
export const formatReportDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
